'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Navigation from '../../components/Navigation';
import {
    DocAnswer,
    DocCode,
    DocEyebrow,
    DocMistakeList,
    DocRelated,
    DocSection,
    DocSubsection,
    DocSubtitle,
    DocText,
    DocTitle,
} from '../../components/DocArticle';
import { encodeBase64 } from '../../utils/base64';

const verifyExampleUrl = (() => {
    const oldEncoded = encodeBase64(JSON.stringify({ name: 'demo', version: 1 }, null, 2));
    const newEncoded = encodeBase64(JSON.stringify({ name: 'demo', version: 2 }, null, 2));
    const patchEncoded = encodeBase64(JSON.stringify([{ op: 'test', path: '/version', value: 1 }, { op: 'replace', path: '/version', value: 2 }], null, 2));
    return `/?old=${oldEncoded}&new=${newEncoded}&patch=${patchEncoded}&mode=verify`;
})();

const mistakes = [
    {
        mistake: 'Unknown operation name',
        fix: 'Only six operation names exist: add, remove, replace, move, copy, test. "update", "delete" or "set" are not valid - use replace, remove and add.',
    },
    {
        mistake: 'Missing value on add, replace or test',
        fix: 'add, replace and test all require a value member. remove needs none, and move and copy need from instead.',
    },
    {
        mistake: 'Unescaped JSON Pointer paths',
        fix: 'Inside a path, ~ and / must be escaped as ~0 and ~1. A key literally named "a/b" is addressed as /a~1b.',
    },
    {
        mistake: 'Patch wrapped in an object',
        fix: 'A patch document is a JSON array of operations, starting with [. Wrapping it in an object like { "patch": [...] } makes it invalid.',
    },
    {
        mistake: 'Trailing commas or comments',
        fix: 'A patch is plain JSON. Trailing commas, // comments and single quotes are all syntax errors.',
    },
];

export default function ValidatePage() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
                    {/* Breadcrumb */}
                    <Box component="nav" sx={{ display: 'flex', mb: 4, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                        <Link href="/" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Home</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Typography component="span" sx={{ color: 'var(--fg)', fontSize: '0.875rem' }}>Validator</Typography>
                    </Box>

                    <Box sx={{ maxWidth: '800px' }}>
                        <DocEyebrow>Tool</DocEyebrow>
                        <DocTitle>JSON Patch Validator</DocTitle>
                        <DocSubtitle>
                            Check that an RFC 6902 patch is well-formed - and, more importantly, that it does what you
                            expect when it meets a real document.
                        </DocSubtitle>

                        <DocAnswer>
                            To validate a JSON Patch, check two things. First the structure: the patch is a JSON array,
                            every operation has a valid <code>op</code> name and <code>path</code>, and the required
                            members (<code>value</code> for add/replace/test, <code>from</code> for move/copy) are present.
                            Then the behaviour: apply or verify the patch against a real document, because a well-formed
                            patch can still fail on missing paths, out-of-range indexes and test mismatches.
                        </DocAnswer>

                        {/* CTA */}
                        <Box sx={{
                            my: 5, p: 4, borderRadius: 'var(--radius-lg)',
                            background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-hover, var(--accent)) 100%)',
                            textAlign: 'center',
                        }}>
                            <Typography variant="h2" sx={{ fontSize: '1.4rem', fontWeight: 700, color: 'white', mb: 1 }}>
                                Validate a patch right now
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 3 }}>
                                The Verify mode loads a source document, your patch and the expected result - and gives you a pass/fail verdict with the exact failing operation.
                            </Typography>
                            <Button
                                size="large"
                                href={verifyExampleUrl}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    backgroundColor: 'white', color: 'var(--accent)', fontWeight: 600,
                                    px: 4, py: 1.5, textTransform: 'none',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Open the validator with an example
                            </Button>
                        </Box>

                        <DocSection>What makes a patch well-formed</DocSection>
                        <DocText>
                            RFC 6902 defines the shape of a patch document. A conforming patch passes all of these
                            checks:
                        </DocText>
                        <Box component="ul" sx={{ color: 'var(--fg-muted)', lineHeight: 1.8, pl: 3, mb: 3 }}>
                            <li>The document is a <strong>JSON array</strong> of operation objects.</li>
                            <li>Every operation has an <code>op</code> member naming one of the six operations: <code>add</code>, <code>remove</code>, <code>replace</code>, <code>move</code>, <code>copy</code>, <code>test</code>.</li>
                            <li>Every operation has a <code>path</code> member holding a valid{' '}<Link href="/docs/json-pointer" style={{ color: 'var(--accent)' }}>JSON Pointer</Link>.</li>
                            <li><code>add</code>, <code>replace</code> and <code>test</code> also carry a <code>value</code>; <code>move</code> and <code>copy</code> carry a <code>from</code> pointer.</li>
                            <li>Members the RFC does not define are ignored - they do not make a patch invalid, but they do nothing.</li>
                        </Box>

                        <DocSection>What well-formed does not guarantee</DocSection>
                        <DocText>
                            Structural validation is only half the story. A perfectly formed patch can still fail against
                            a specific document: the path it removes may not exist, an array index may be out of range,
                            or a <code>test</code> operation may find a different value than expected - including type
                            mismatches like <code>1</code> vs <code>&quot;1&quot;</code>. Those are runtime failures, not
                            syntax errors, and the only way to catch them is to run the patch against the real document.
                        </DocText>
                        <DocText>
                            That is exactly what the Verify mode does: paste the source document, the patch and the
                            result you expect, and it tells you whether the patch gets you there - and where it breaks
                            if it does not. The{' '}
                            <Link href="/docs/json-patch-errors" style={{ color: 'var(--accent)' }}>JSON Patch errors guide</Link>{' '}
                            explains every failure message you can hit.
                        </DocText>

                        <DocSection>An invalid patch, annotated</DocSection>
                        <DocCode label="Three errors in four lines">{`[
  { "op": "update", "path": "/version", "value": 2 },
  { "op": "add", "path": "/tags/-" },
  { "op": "replace", "path": "/a/b/timeout", "value": 60 },
]`}</DocCode>
                        <DocText>
                            The first operation uses <code>update</code>, which does not exist - the name is{' '}
                            <code>replace</code>. The second is an <code>add</code> without a <code>value</code>. The
                            third targets a key literally named <code>a/b</code> without escaping the slash - the path
                            should be <code>/a~1b/timeout</code>. And the trailing comma after the last operation makes
                            the whole document unparseable JSON.
                        </DocText>

                        <DocSection>Tested failure</DocSection>
                        <DocText>
                            Verified 19 September 2026 with fast-json-patch 3.1.1. Against <code>{'{ "version": 2 }'}</code>, a first operation of <code>{'{ "op": "test", "path": "/version", "value": 1 }'}</code> produced <code>TEST_OPERATION_FAILED</code>; the following replace operation was not evaluated.
                        </DocText>

                        <DocSection>The most common validation errors</DocSection>
                        <DocMistakeList items={mistakes} />

                        <DocSection>Frequently asked questions</DocSection>
                        <DocSubsection>Does a valid patch always apply cleanly?</DocSubsection>
                        <DocText>
                            No. Validity is about the patch document itself. Applying also depends on the target
                            document: every <code>remove</code>, <code>replace</code> and <code>test</code> path must
                            exist, and array indexes must be in range. Verify against the real document to be sure.
                        </DocText>
                        <DocSubsection>Do I have to include test operations?</DocSubsection>
                        <DocText>
                            They are optional, but they are the only way to make a patch fail safely when the document
                            has drifted. For anything that writes to shared or production data, guard the assumptions
                            with <Link href="/operations/test" style={{ color: 'var(--accent)' }}>test</Link> first.
                        </DocText>
                        <DocSubsection>Can I validate a patch without applying it?</DocSubsection>
                        <DocText>
                            Structurally, yes - the checks above are enough. But the only way to know a patch produces
                            the document you want is to run it. Verify mode does that in your browser without sending
                            anything to a server.
                        </DocText>

                        <DocRelated links={[
                            { href: '/docs/json-patch-errors', title: 'JSON Patch errors', description: 'Every failure message and how to fix it' },
                            { href: '/docs/how-to-apply-json-patch', title: 'How to apply a JSON Patch', description: 'Sequential semantics, validation and test preconditions' },
                            { href: '/operations/test', title: 'Test operation', description: 'Guard writes against unexpected document state' },
                            { href: '/examples', title: 'Examples', description: 'Real-world patches you can verify live' },
                            { href: '/docs', title: 'Documentation', description: 'Back to the JSON Patch documentation hub' },
                        ]} />
                    </Box>
                </Container>
            </main>
        </>
    );
}
